// src/components/CustomerTabNav.js
import React, { useContext, useState } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import logo from '../assets/logo.png';

/**
 * CustomerTabNav - Tabbed navigation bar for customer users
 *
 * Features:
 * - Horizontal tab layout for all screen sizes
 * - Clean, persistent navigation without hamburger menu
 * - User profile dropdown on the right
 * - Active tab highlighting
 * - Light mode styling (consistent across themes)
 * - Responsive design (mobile & desktop)
 */
const CustomerTabNav = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [profileOpen, setProfileOpen] = useState(false);

  const handleLogout = async () => {
    setProfileOpen(false);
    // A kiosk leaves kiosk mode from its profile screen, with the kiosk password.
    if (user?.role === 'kiosk') {
      navigate('/kiosk/profile');
      return;
    }
    await logout();
    navigate('/');
  };

  // Navigation tabs for customers
  const navTabs = [
    { label: 'Home', path: '/' },
    { label: 'Vehicles', path: '/vehicles' },
    { label: 'Dealers', path: '/dealers/search' },
    { label: 'Favorites', path: '/favorites' },
    { label: 'Appointments', path: '/appointments' },
    { label: 'Profile', path: '/profile' },
  ];

  return (
    <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-gray-200/50 customer-friendly">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo - Hidden on mobile to save space */}
          <Link to="/" className="hidden sm:flex items-center flex-shrink-0 p-3">
            <img
              src={logo}
              alt="SelfDrive"
              className="h-14 min-w-[45px] w-auto"
              style={{ filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))' }}
            />
          </Link>

          {/* Navigation Tabs */}
          <div className="flex items-center space-x-1 sm:space-x-2 flex-1 sm:flex-initial justify-around sm:justify-center">
            {navTabs.map((tab) => (
              <NavLink
                key={tab.path}
                to={tab.path}
                end={tab.path === '/'}
                className={({ isActive }) =>
                  `px-3 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-medium transition-all duration-300 ${
                    isActive
                      ? 'bg-gradient-to-r from-customer-teal to-customer-teal-hover text-white shadow-lg shadow-customer-teal/30'
                      : 'text-gray-600 hover:bg-customer-teal-light hover:text-customer-teal-active'
                  }`
                }
              >
                {tab.label}
              </NavLink>
            ))}
          </div>

          {/* User Profile Dropdown or Auth Buttons */}
          {user ? (
            <div className="relative flex-shrink-0">
              <button
                onClick={() => setProfileOpen(!profileOpen)}
                className="flex items-center space-x-2 px-2 sm:px-3 py-2 rounded-xl hover:bg-customer-teal-light transition-all duration-300"
                aria-label="User menu"
              >
                <div className="w-8 h-8 bg-gradient-to-br from-customer-accent to-customer-accent-hover rounded-full flex items-center justify-center text-white text-sm font-semibold shadow-md">
                  {user.name ? user.name[0].toUpperCase() : 'U'}
                </div>
                <span className="hidden md:inline text-sm text-gray-900 max-w-[120px] truncate">
                  {user.name || user.email}
                </span>
                <svg
                  className={`hidden md:inline w-4 h-4 text-gray-600 transition-transform ${
                    profileOpen ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {/* Profile Dropdown Menu */}
              {profileOpen && (
                <>
                  {/* Backdrop */}
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setProfileOpen(false)}
                  />

                  {/* Dropdown Items */}
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                    <div className="px-4 py-2 border-b border-gray-200">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {user.name || 'User'}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {user.email}
                      </p>
                    </div>
                    <Link
                      to="/profile"
                      onClick={() => setProfileOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      Settings
                    </Link>
                    <div className="border-t border-gray-200 my-1"></div>
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 transition-colors"
                    >
                      Logout
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            // Login/Register buttons for non-logged-in users
            <div className="flex items-center space-x-2 flex-shrink-0">
              <Link
                to="/login"
                className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 hover:text-customer-teal transition-colors"
              >
                Login
              </Link>
              <Link
                to="/register"
                className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold bg-gradient-to-r from-customer-accent to-customer-accent-hover text-white rounded-xl hover:from-customer-accent-hover hover:to-customer-accent-active transition-all duration-300 shadow-lg shadow-customer-accent/30"
              >
                Sign Up
              </Link>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default CustomerTabNav;
